import React from 'react';
import VideoPlayer from './VideoPlayer';
import { useModalA11y } from '../hooks/useModalA11y';
import './MediaDialog.css';

interface MediaDialogProps {
  src: string;
  poster?: string;
  /** Accessible dialog name. */
  title: string;
  onClose: () => void;
  loop?: boolean;
  /**
   * Set false while a nested modal (e.g. upload) is open so it owns Escape,
   * the focus trap and the keyboard shortcuts.
   */
  active?: boolean;
  /** Info panel content shown beside (desktop) or below (mobile) the video. */
  children?: React.ReactNode;
}

/** Keyboard cheat-sheet for the practice controls (hidden on touch devices). */
export const ShortcutHint: React.FC = () => (
  <p className="media-dialog-hint">
    <kbd>K</kbd> play · <kbd>[</kbd> <kbd>]</kbd> speed · <kbd>R</kbd> mirror · <kbd>A</kbd> loop section ·{' '}
    <kbd>,</kbd> <kbd>.</kbd> frame step · <kbd>F</kbd> fullscreen
  </p>
);

/** Theater-style modal: video on a dark stage with an info panel. */
const MediaDialog: React.FC<MediaDialogProps> = ({
  src,
  poster,
  title,
  onClose,
  loop = true,
  active = true,
  children,
}) => {
  const containerRef = useModalA11y(onClose, active);

  return (
    <div className="media-dialog-overlay" onClick={onClose}>
      <div
        ref={containerRef}
        className="media-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="media-dialog-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
            <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
        <div className="media-dialog-stage">
          <VideoPlayer
            src={src}
            poster={poster}
            title={title}
            autoPlay
            loop={loop}
            keyboard={active ? 'global' : 'focus'}
          />
        </div>
        {children && <div className="media-dialog-panel">{children}</div>}
      </div>
    </div>
  );
};

export default MediaDialog;
